import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import {
    useFloating,
    autoUpdate,
    flip,
    shift,
    useDismiss,
    useRole,
    useClick,
    useInteractions,
    useId,
    FloatingFocusManager,
} from '@floating-ui/react';
import { Icons } from '@shared/ui';
import i18n from '@shared/i18n';
import { useNavigationContext } from './useNavigation';
import { MenuItem } from './MenuItem';

interface MenuMobileGroupProps {}

export const MenuMobileGroup: React.FC<MenuMobileGroupProps> = () => {
    const { state, actions } = useNavigationContext();

    const [popperEL, setPopperEl] = useState<HTMLElement | null>(null);

    const { refs, floatingStyles, context } = useFloating({
        placement: 'top-start',
        elements: {
            floating: popperEL,
        },
        open: !!popperEL,
        transform: false,
        onOpenChange: (open) => {
            setPopperEl(open ? popperEL : null);
        },
        middleware: [
            flip({
                crossAxis: false,
                fallbackAxisSideDirection: 'start',
            }),
            shift(),
        ],
        whileElementsMounted: (referenceEl, floatingEl, update) => {
            const cleanup = autoUpdate(referenceEl, floatingEl, update);

            return cleanup;
        },
    });

    useEffect(() => {
        const element = document.querySelector('[data-section-header]');

        element && refs?.setReference(element);
    }, [state.isHeaderNav, state.isFooterNav]);

    const headingId = useId();

    const click = useClick(context);

    const dismiss = useDismiss(context);

    const role = useRole(context);

    const { getFloatingProps } = useInteractions([click, dismiss, role]);

    return (
        <FloatingFocusManager context={context} modal={false}>
            <div
                ref={refs.setFloating}
                style={floatingStyles}
                aria-labelledby={headingId}
                {...getFloatingProps()}
            >
                <div
                    role="button"
                    tabIndex={0}
                    className="menu-backdrop"
                    onClick={actions.handleMenuStatusReset}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            actions.handleMenuStatusReset();
                        }
                    }}
                />
                <ul
                    className={clsx('menu-list', 'menu-list--responsive', {
                        'menu-list--responsive-open': state.isMobileMenuOpen,
                    })}
                >
                    <>
                        {!!state.currentNode.length && (
                            <>
                                <button
                                    type="button"
                                    className="menu-mobile-back"
                                    onClick={actions.handleMenuBack}
                                >
                                    <Icons.csr.CaretLeft size="20px" weight="bold" />
                                    <span>{i18n.gettext('返回')}</span>
                                </button>
                                <div className="menu-mobile-divider" />
                            </>
                        )}
                        {(
                            state.navigationGroup[state.currentNodeLevel] ||
                            state.navigation?.next ||
                            []
                        ).map((item) => (
                            <MenuItem
                                key={`${item.value}-${item.name}`}
                                {...item}
                                showOpenInNewTab={state.openInNewTab}
                                levelIndex={state.currentNode.length}
                            >
                                {item.name}
                            </MenuItem>
                        ))}
                    </>
                </ul>
            </div>
        </FloatingFocusManager>
    );
};
